import { Box, Button, Stack } from '@mui/material';
import {
  Form,
  ModalDialogWrapper,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import {
  forwardRef,
  useState,
  useImperativeHandle,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { NOTE_ACTION } from '@constants/notesAttachment';
import IconLibrary from '../../IconLibrary';
import schema from './schemas/edit.json';
import validationSchema from './lib/validations';
import getDefaultValuesForForm from '../../../utils/getDefaultValuesForForm';
import { saveNote, uploadPrivateDocument } from './lib/lib';
import NoteAttachmentList from './NoteAttachmentList';

const FORM_ID = 'ServiceJobNoteForm';

const NoteDetailModal = forwardRef(({ serviceJobId, noteListRef }, ref) => {
  const defaultInitialValues = {
    note: '',
    files: [],
  };
  const formRef = useRef(null);
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, post, getApiUrl } = useDataProvider(provider);

  const commonParams = {
    put,
    post,
    getApiUrl,
  };

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [action, setAction] = useState(NOTE_ACTION.EDIT);
  const [note, setNote] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  const closeModal = () => {
    setOpen(false);
    setNote(null);
    setAction(NOTE_ACTION.EDIT);
    setCanEdit(false);
  };

  const onSubmit = async (data, additional) => {
    const { saveAndClose = false } = additional;
    try {
      const saveNoteResponse = await saveNote({
        params: commonParams,
        id: note?.id,
        note: data?.note,
        action,
        serviceJobId,
      });

      if (saveNoteResponse?.ok) {
        const uploadResponse = await Promise.all(
          data.files.map((element, index) => {
            return uploadPrivateDocument({
              params: commonParams,
              serviceJobId,
              noteId:
                action === NOTE_ACTION.ADD
                  ? saveNoteResponse?.data?.id
                  : note?.id,
              fileName: element?.file?.name,
              file: element?.file,
              attachmentType: data.files[index]?.attachmentType,
              description: data.files[index]?.description || null,
              fileSize: element?.file?.size,
            });
          }),
        );

        const errors = uploadResponse.flatMap(item =>
          Array.isArray(item?.errors)
            ? item.errors.map(err => ({
                path: err.path,
                message: err.message
                  ? `${err.message} (${err.path})`
                  : 'Unknown error',
                code: err.errorCode,
              }))
            : [],
        );

        if (Array.isArray(errors) && errors?.length > 0) {
          return {
            nextValues: data,
            errors,
          };
        }

        if (action === NOTE_ACTION.ADD) {
          snackbar.showMessage(
            formatMessage({
              id: 'NOTE_WAS_ADDED_SUCCESSFULLY',
              defaultMessage: 'Note was added successfully',
            }),
          );
        } else {
          snackbar.showMessage(
            formatMessage({
              id: 'NOTE_WAS_UPDATED_SUCCESSFULLY',
              defaultMessage: 'Note was updated successfully',
            }),
          );
        }

        const nextValues = {
          ...saveNoteResponse?.data,
          noteId: saveNoteResponse?.data?.id,
          files: [],
          serviceJobId,
        };
        return {
          nextValues,
          errors: null,
          callback: () => {
            noteListRef?.current?.initialize();
            if (saveAndClose) {
              closeModal();
            } else if (action === NOTE_ACTION.ADD) {
              setNote(saveNoteResponse?.data);
              setAction(NOTE_ACTION.EDIT);
            }
          },
        };
      }
      return {
        nextValues: data,
        errors: saveNoteResponse?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err?.message }],
      };
    }
  };

  useImperativeHandle(ref, () => ({
    open: ({ note: noteData, canEdit: canEditArg, action: noteAction }) => {
      setNote(noteData);
      setCanEdit(canEditArg);
      setAction(noteAction);
      setOpen(true);
    },
    close: () => {
      closeModal();
    },
  }));

  const defaultValues = useMemo(
    () => ({
      ...getDefaultValuesForForm({
        schema,
      }),
      ...defaultInitialValues,
      ...(note?.id && { note: note?.note, noteId: note?.id }),
      files: [],
      serviceJobId,
    }),
    [JSON.stringify(note), open, action, serviceJobId],
  );

  const staticPermissions = {
    files: !canEdit ? 'READ' : 'WRITE',
    note: !canEdit ? 'READ' : 'WRITE',
  };

  useEffect(() => {
    if (action === NOTE_ACTION.EDIT) {
      setTitle('Edit Note');
    } else if (action === NOTE_ACTION.ADD) {
      setTitle(
        formatMessage({
          id: 'ADD_NOTE',
          defaultMessage: 'Add Note',
        }),
      );
    } else {
      setTitle('View Note');
    }
  }, [action]);

  if (!open) {
    return null;
  }

  return (
    <ModalDialogWrapper
      title={title}
      Icon={IconLibrary.Note}
      open={open}
      width="xl"
      close={closeModal}>
      <Box sx={{ px: 2 }}>
        <Form
          dialog
          staticPermissions={staticPermissions}
          customComponents={{
            AttachmentList: NoteAttachmentList,
          }}
          formId={FORM_ID}
          ref={formRef}
          onSubmit={onSubmit}
          schema={schema}
          defaultValues={defaultValues}
          validationSchema={validationSchema(formatMessage)}>
          <Box>
            <Stack
              gap={1}
              direction="row"
              justifyContent="center"
              sx={{
                mt: 3,
              }}>
              {canEdit && (
                <Button
                  type="button"
                  onClick={() => formRef.current.submit({ formId: FORM_ID })}
                  startIcon={<IconLibrary.Save />}
                  variant="contained">
                  <FormattedMessage id="SAVE" defaultMessage="Save" />
                </Button>
              )}
              {canEdit && (
                <Button
                  onClick={() =>
                    formRef.current.submit({
                      formId: FORM_ID,
                      saveAndClose: true,
                    })
                  }
                  startIcon={<IconLibrary.Save />}
                  variant="contained">
                  <FormattedMessage
                    id="SAVE_AND_CLOSE"
                    defaultMessage="Save and close"
                  />
                </Button>
              )}
              {canEdit && (
                <Button
                  onClick={() => formRef.current.reset({ formId: FORM_ID })}
                  startIcon={<IconLibrary.Reset />}
                  variant="outlined">
                  <FormattedMessage id="RESET" defaultMessage="Reset" />
                </Button>
              )}
              <Button
                color="tertiary"
                onClick={() => closeModal()}
                startIcon={<IconLibrary.Close />}
                variant="outlined">
                <FormattedMessage id="CLOSE" defaultMessage="Close" />
              </Button>
            </Stack>
          </Box>
        </Form>
      </Box>
    </ModalDialogWrapper>
  );
});

export default NoteDetailModal;
